import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import dayjs from 'dayjs';

import HeadSub from '../../../../components/comm/HeadSub';
import {
    getExerciseTypes,
    saveExercise,
    updateExercise,
    EXRC_IN_CD,
    toRecDe,
    nowRecTm,
    getLatestWeight,
    toIntensityKey,
} from '../../../../api/exerciseAPI';

import URL from '../../../../constants/url';
import style from './ExerciseRecordAdd.module.css';
import iconRpe01 from '../../../../images/common/i_RPE_01.svg';
import iconRpe02 from '../../../../images/common/i_RPE_02.svg';
import iconRpe03 from '../../../../images/common/i_RPE_03.svg';

const INTENSITY = [
    { key: 'light',  icon: iconRpe01, label: '가볍게', desc: '대화가 편해요' , met: 2.5,},
    { key: 'normal', icon: iconRpe02, label: '보통',   desc: '약간 숨이 차요' ,  met: 5.0,},
    { key: 'hard',   icon: iconRpe03, label: '격하게', desc: '많이 힘들어요', met: 7.5, },
];

/**
 * Half-even 반올림 (은행가 반올림)
 * 소수점이 정확히 .5이면 가장 가까운 짝수로 반올림
 */
const roundHalfEven = (value) => {
    if (!Number.isFinite(value)) return 0;

    const integer = Math.floor(value);
    const fraction = value - integer;

    // 부동소수점 오차 보정
    const epsilon = 1e-10;

    if (Math.abs(fraction - 0.5) < epsilon) {
        return integer % 2 === 0 ? integer : integer + 1;
    }

    return Math.round(value);
};

const QUICK_MINS = [5, 10, 30, 60];

/** 강도별 분당 소모 칼로리. 종목과 무관한 공통값이다. */
// const KCAL_PER_MIN = { light: 5, normal: 8, hard: 12 };

/** 검색 요청을 타이핑마다 보내지 않도록 기다리는 시간(ms). */
const SEARCH_DELAY = 250;

// kcal/min로 강도 역산 (저장 데이터에 intensity 없을 때)
// const inferIntensity = (kcal, mins) => {
//     if (!mins) return '';
//     const perMin = kcal / mins;
//     // KCAL_PER_MIN: light 5, normal 8, hard 12 — 가장 가까운 값
//     const diffs = Object.entries(KCAL_PER_MIN).map(([k, v]) => [k, Math.abs(v - perMin)]);
//     diffs.sort((a, b) => a[1] - b[1]);
//     return diffs[0][0];
// };

const ExerciseRecordAdd = () => {
    const navigate = useNavigate();
    const { state } = useLocation();
    const isEdit = state?.mode === 'edit';
    const editItem = state?.item || null;

    /** 어느 날짜에 기록할지. 기록 화면에서 넘겨주고, 없으면 오늘. */
    const recDe = state?.recDe || toRecDe(dayjs().format('YYYY-MM-DD'));

    // 검색
    const [query, setQuery] = useState(editItem?.name || '');
    // const [selectedExercise, setSelectedExercise] = useState(editItem?.name || '');

    const [selectedExercise, setSelectedExercise] = useState(null);

    // 환자 체중
    const [weight, setWeight] = useState(null);
    const [weightLoading, setWeightLoading] = useState(true);

    const [showResults, setShowResults] = useState(false);
    const [results, setResults] = useState([]);   // 서버에서 받은 종목 목록
    const [searched, setSearched] = useState(false); // 검색이 한 번이라도 끝났는지
    const [recentSearches, setRecentSearches] = useState(() => {
        try { return JSON.parse(localStorage.getItem('recentExercises') || '[]'); }
        catch { return []; }
    });

    const inputRef = useRef(null);

    // 시간
    const [minutes, setMinutes] = useState(editItem?.minutes || 0);

    // 강도
    // const [intensity, setIntensity] = useState(
    //     editItem ? (editItem.intensity || inferIntensity(editItem.kcal, editItem.minutes)) : ''
    // );

    const [intensity, setIntensity] = useState(
        editItem
            ? (editItem.intensity ||
            toIntensityKey(editItem.exrcInCd))
            : ''
    );

    const [saving, setSaving] = useState(false);

    /** 입력이 멈추면 서버에서 종목을 찾아온다. */
    useEffect(() => {
        const keyword = query.trim();
        if (!showResults || !keyword) {
            setResults([]);
            setSearched(false);
            return;
        }

        const timer = setTimeout(async () => {
            try {
                setResults(await getExerciseTypes(keyword));
            } catch (e) {
                console.error('운동 종목 검색 실패', e);
                setResults([]);
            } finally {
                setSearched(true);
            }
        }, SEARCH_DELAY);

        return () => clearTimeout(timer);
    }, [query, showResults]);

    /** 목록에서 고르지 않고 직접 친 이름도 그대로 기록할 수 있다. */
    // const exerciseName = selectedExercise || query.trim();
    const exerciseName = selectedExercise?.exrcNm || query.trim();

    // const estimatedKcal = minutes > 0 && intensity
    //     ? minutes * (KCAL_PER_MIN[intensity] || 0)
    //     : 0;

    // 운동별 MET
    const exerciseMet = Number(selectedExercise?.exrcMetVal);

    // 선택한 운동 강도의 기본 MET
    const intensityMet = INTENSITY.find(
        (item) => item.key === intensity
    )?.met;

    // 실제 계산에 사용할 MET
    const met =
        selectedExercise?.exrcMetVal != null &&
        selectedExercise?.exrcMetVal !== '' &&
        Number.isFinite(exerciseMet) &&
        exerciseMet > 0
            ? exerciseMet
            : intensityMet;

    // 예상 소모 칼로리
    const estimatedKcal =
        weight > 0 &&
        minutes > 0 &&
        met > 0
            ? roundHalfEven(
                met * weight * 3.5 / 200 * minutes
            )
        : 0;

    const saveRecent = (name) => {
        const next = [name, ...recentSearches.filter((r) => r !== name)].slice(0, 10);
        setRecentSearches(next);
        localStorage.setItem('recentExercises', JSON.stringify(next));
    };

    const removeRecent = (name) => {
        const next = recentSearches.filter((r) => r !== name);
        setRecentSearches(next);
        localStorage.setItem('recentExercises', JSON.stringify(next));
    };

    const clearAllRecent = () => {
        setRecentSearches([]);
        localStorage.removeItem('recentExercises');
    };

    // const handleSelect = (name) => {
    //     setSelectedExercise(name);
    //     setQuery(name);
    //     setShowResults(false);
    //     saveRecent(name);
    // };

    const handleSelect = (exercise) => {
        setSelectedExercise(exercise);
        setQuery(exercise.exrcNm);
        setShowResults(false);
        saveRecent(exercise.exrcNm);
    };

    const handleSearch = () => {
        if (!query.trim()) return;
        setShowResults(true);
    };

    const handleClearQuery = () => {
        setQuery('');
        setSelectedExercise(null);
        setShowResults(false);
        inputRef.current?.focus();
    };

    const adjustMinutes = (delta) => {
        setMinutes((prev) => Math.max(0, prev + delta));
    };

    /** 저장 직전에 운동명으로 종목 ID를 찾는다. 목록에 없으면 null 로 둔다. */
    const resolveExrcMngId = async (name) => {
        try {
            const list = await getExerciseTypes(name);
            return list.find((t) => t.exrcNm === name)?.exrcMngId ?? null;
        } catch {
            return null;
        }
    };

    const handleSubmit = async () => {
        // if (!exerciseName || minutes <= 0 || !intensity || saving) return;
        if (!exerciseName || minutes <= 0 || !intensity || weightLoading || saving) return;

        if (!weight || weight <= 0) {
            alert('체중 정보를 확인할 수 없어 소모 칼로리를 계산할 수 없습니다.');
            return;
        }

        setSaving(true);
        try {
            const exrcMngId = selectedExercise?.exrcMngId ?? await resolveExrcMngId(exerciseName);

            const record = {
                recDe: isEdit ? (editItem.recDe || recDe) : recDe,
                recTm: nowRecTm(),
                exrcMngId,
                exrcNm: exerciseName,
                exrcMin: minutes,
                exrcInCd: EXRC_IN_CD[intensity],
                expctCalor: estimatedKcal,
                cnsumCalor: estimatedKcal,
                cntcRecAt: '0', // 앱에서 직접 입력한 기록
            };

            const ok = isEdit
                ? await updateExercise(editItem.id, record)
                : await saveExercise(record);

            if (!ok) {
                alert(isEdit ? '수정하지 못했습니다.' : '저장하지 못했습니다.');
                return;
            }

            saveRecent(exerciseName);

            if (isEdit) {
                navigate(-1);
            } else {
                navigate(URL.EXERCISE_RECORD_COMPLETE, {
                    state: { name: exerciseName, kcal: estimatedKcal },
                    replace: true,
                });
            }
        } catch (e) {
            console.error('운동 기록 저장 실패', e);
            alert('저장하지 못했습니다.');
        } finally {
            setSaving(false);
        }
    };

    const isSearching = showResults && query.trim();


    useEffect(() => {
        let active = true;

        const fetchWeight = async () => {
            try {
                const result = await getLatestWeight();

                if (!active) return;

                const value = Number(result?.bdwgh);

                setWeight(
                    result?.bdwgh != null &&
                    result?.bdwgh !== '' &&
                    Number.isFinite(value) &&
                    value > 0
                        ? value
                        : null
                );
            } catch (error) {
                console.error('최신 체중 조회 실패', error);

                if (active) {
                    setWeight(null);
                }
            } finally {
                if (active) {
                    setWeightLoading(false);
                }
            }
        };

        fetchWeight();

        return () => {
            active = false;
        };
    }, []);

    const handleRecentSelect = async (name) => {
        try {
            const list = await getExerciseTypes(name);

            const exercise = list.find(
                (item) => item.exrcNm === name
            );

            if (exercise) {
                handleSelect(exercise);
            } else {
                setSelectedExercise(null);
                setQuery(name);
                setShowResults(false);
            }
        } catch (error) {
            console.error('최근 운동 조회 실패', error);

            setSelectedExercise(null);
            setQuery(name);
            setShowResults(false);
        }
    };

    useEffect(() => {
        if (!isEdit || !editItem?.name) return;

        let active = true;

        const fetchEditExercise = async () => {
            try {
                const list = await getExerciseTypes(editItem.name);

                if (!active) return;

                const exercise = list.find(
                    (item) =>
                        item.exrcMngId === editItem.exrcMngId
                ) || list.find(
                    (item) => item.exrcNm === editItem.name
                );

                if (exercise) {
                    setSelectedExercise(exercise);
                }
            } catch (error) {
                console.error('수정 운동 정보 조회 실패', error);
            }
        };

        fetchEditExercise();

        return () => {
            active = false;
        };
    }, [isEdit, editItem?.name, editItem?.exrcMngId]);

    return (
        <>
            <HeadSub title={isEdit ? '운동 수정' : '운동 추가'} />
            <div id="sub-content" data-layout="white">
                <div className="inner-wrap" style={{paddingTop:'0'}}>

                    {/* ── 운동 검색 ── */}
                    <section className={style.section}>
                        <h3 className={style.sectionTitle}>어떤 운동을 했나요?</h3>
                        <div className={style.searchBox}>
                            <input
                                ref={inputRef}
                                type="text"
                                className={style.searchInput}
                                placeholder="운동명을 입력해주세요"
                                value={query}
                                onChange={(e) => {
                                    setQuery(e.target.value);
                                    setShowResults(true);
                                    setSelectedExercise(null);
                                }}
                                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                            />
                            {query && (
                                <button className={style.clearBtn} onClick={handleClearQuery}>
                                    <span className="blind">지우기</span>
                                </button>
                            )}
                            <button className={style.searchBtn} onClick={handleSearch}>
                                <span className="blind">검색</span>
                            </button>
                        </div>

                        {/* 검색 결과 — 전체 영역 */}
                        {isSearching && (
                            <ul className={style.resultList}>
                                {results.length > 0 ? (
                                    results.map((type) => (
                                        <li key={type.exrcMngId}>
                                            <button className={style.resultItem} onClick={() => handleSelect(type)}>
                                                <span className={style.resultIcon} />
                                                <span>{type.exrcNm}</span>
                                            </button>
                                        </li>
                                    ))
                                ) : (
                                    <li className={style.resultEmpty}>
                                        <span className={style.resultIcon} />
                                        {searched ? '해당 운동이 목록에 없습니다' : '검색 중...'}
                                    </li>
                                )}
                            </ul>
                        )}

                        {/* 최근 검색어 */}
                        {!isSearching && recentSearches.length > 0 && (
                            <div className={style.recentBox}>
                                <div className={style.recentHead}>
                                    <span>최근 검색어</span>
                                    <button onClick={clearAllRecent}>전체 삭제</button>
                                </div>
                                <div className={style.recentTags}>
                                    {recentSearches.map((r) => (
                                        <span key={r} className={style.recentTag}>
                                            <button onClick={() => handleRecentSelect(r)}>{r}</button>
                                            <button className={style.tagRemove} onClick={() => removeRecent(r)}>×</button>
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}
                    </section>

                     {/* ── 시간/강도 — 검색 중이면 숨김 ── */}
                        {!isSearching && (
                            <>
                                <section className={style.section}>
                                    <h3 className={style.sectionTitle}>운동을 얼마나 했나요?</h3>
                                    <div className={style.durationRow}>
                                        <button className={style.durationBtn} data-duration='minus' onClick={() => adjustMinutes(-1)}>−</button>
                                        <div className={style.durationValue}>
                                            <span className={style.durationNum}>{minutes}</span>
                                            <span className={style.durationUnit}>분</span>
                                        </div>
                                        <button className={style.durationBtn} data-duration='plus' onClick={() => adjustMinutes(1)}>+</button>
                                    </div>
                                    <div className={style.quickBtns}>
                                        {QUICK_MINS.map((m) => (
                                            <button key={m} className={style.quickBtn} onClick={() => adjustMinutes(m)}>
                                                +{m}분
                                            </button>
                                        ))}
                                    </div>
                                </section>

                                {/* ── 강도 선택 ── */}
                                <section className={style.section}>
                                    <h3 className={style.sectionTitle}>운동 강도는 어땠나요?</h3>
                                    <div className={style.intensityRow}>
                                        {INTENSITY.map((item) => (
                                            <button
                                                key={item.key}
                                                className={`${style.intensityBtn} ${intensity === item.key ? style.intensityActive : ''}`}
                                                onClick={() => setIntensity(item.key)}
                                            >
                                                <img className={style.intensityIcon} src={item.icon} alt="운동 강도 이미지" />
                                                <span className={style.intensityLabel}>{item.label}</span>
                                                <span className={style.intensityDesc}>{item.desc}</span>
                                            </button>
                                        ))}
                                    </div>
                                </section>
                            </>
                        )}
                </div>
            </div>

            {/* ── 하단 바 ── */}
            {!isSearching && (
                <div className={style.bottomBar}>
                    <div className={style.kcalPreview}>
                        예상 소모 칼로리 <strong>{estimatedKcal.toLocaleString()} kcal</strong>
                    </div>
                    <button
                        type="button"
                        className={style.btnPrimary}
                        // disabled={!exerciseName || minutes <= 0 || !intensity || saving}
                        disabled={!exerciseName || minutes <= 0 || !intensity || weightLoading || !weight || saving}
                        onClick={handleSubmit}
                    >
                        {isEdit ? '수정 완료' : '기록 완료'}
                    </button>
                </div>
            )}
        </>
    );
};

export default ExerciseRecordAdd;
